import { createToaster, Toaster as ChakraToaster } from '@chakra-ui/react';
import { HapticFeedback } from './HapticFeedback';
import { Capacitor } from '@capacitor/core';

// Create a toast instance for Chakra UI v3
const toaster = createToaster({
    placement: 'top',
    duration: 3000,
});

const isNative = Capacitor.isNativePlatform();

interface ToastOptions {
    title: string;
    description?: string;
    duration?: number;
    isClosable?: boolean;
    action?: {
        label: string;
        onClick: VoidFunction;
    };
}

/**
 * Native-style Toast Notification Service
 * Replacement for window.alert() with better UX
 * 
 * Usage:
 * ToastService.success({ title: 'Gespeichert!' });
 * ToastService.error({ title: 'Fehler', description: 'Etwas ist schiefgegangen' });
 * ToastService.info({ title: 'Info', description: 'Neue Nachricht' });
 * ToastService.warning({ title: 'Achtung', description: 'Bitte überprüfen' });
 */
export const ToastService = {
    /**
     * Success toast with green color and success haptic
     */
    success: (options: ToastOptions) => {
        if (isNative) {
            HapticFeedback.success();
        }
        
        toaster.success({
            title: options.title,
            description: options.description,
            duration: options.duration || 3000,
            action: options.action,
        });
    },

    /**
     * Error toast with red color and error haptic
     */
    error: (options: ToastOptions) => {
        if (isNative) {
            HapticFeedback.error();
        }
        
        toaster.error({
            title: options.title,
            description: options.description,
            duration: options.duration || 4000,
            action: options.action,
        });
    },

    /**
     * Warning toast with orange/yellow color and warning haptic
     */
    warning: (options: ToastOptions) => {
        if (isNative) {
            HapticFeedback.warning();
        }
        
        toaster.warning({
            title: options.title,
            description: options.description,
            duration: options.duration || 4000,
            action: options.action,
        });
    },

    /**
     * Info toast with blue color and light haptic
     */
    info: (options: ToastOptions) => {
        if (isNative) {
            HapticFeedback.light();
        }
        
        toaster.info({
            title: options.title,
            description: options.description,
            duration: options.duration || 3000,
            action: options.action,
        });
    },

    /**
     * Loading toast (persistent until dismissed)
     */
    loading: (options: ToastOptions) => {
        return toaster.loading({
            title: options.title,
            description: options.description,
            duration: options.duration || 999999, // Very long duration (effectively persistent)
            action: options.action,
        });
    },

    /**
     * Close a specific toast by ID
     */
    close: (toastId: string) => {
        toaster.dismiss(toastId);
    },

    /**
     * Close all toasts
     */
    closeAll: () => {
        toaster.dismiss();
    },
};

// Export the toaster instance for use in App.tsx
export { toaster };

// Export the Toaster component to render in App.tsx
export const Toaster = ChakraToaster;
